import { checkIsSideBackgroundDark, getCSSVariable } from './colorUtils'

import { app } from '@/composition/app'
import { GroupState } from '@/composition/groups'
import { Theme } from '@/composition/themes'
import { UserState } from '@/composition/users'
import chroma from 'chroma-js'

export async function updateColorTokens(
  mode?: string,
  preview = false
): Promise<void> {
  // Prefrence order -> passed mode(While toggling, group settings, testModes) > user preference(Only if user logged in) > system preference
  if (!mode) {
    if (UserState.state?.currentUser) {
      mode = UserState.state?.currentUser?.darkMode ? 'dark' : 'light'
    } else {
      const isSystemInDarkMode = window.matchMedia(
        '(prefers-color-scheme: dark)'
      ).matches
      mode = isSystemInDarkMode ? 'dark' : 'light'
    }
  }
  if (app.state.isMobile) {
    mode = 'light'
  }
  toggleShimmerColorScheme(mode !== 'dark')
  let color = getDefaultColors(mode)
  let groupData
  if (preview) {
    groupData = GroupState.state.updatedGroupData
  } else {
    groupData = GroupState.state.groupData
  }
  if (
    !app.state.isMobile &&
    groupData?.branding?.color &&
    groupData?.branding?.color[mode as string]
  ) {
    color = {
      ...color,
      ...groupData?.branding?.color[mode as string],
    }
  }

  document.documentElement.style.setProperty(
    '--sidebar-background',
    color?.sidebarBackground || '#f9fafb'
  )

  document.documentElement.style.setProperty(
    '--primary',
    color?.primary || '#e3ecfc'
  )
  document.documentElement.style.setProperty(
    '--sidebar-background-secondary',
    getLightColor(color?.sidebarBackground || '#f9fafb')
  )

  document.documentElement.style.setProperty(
    '--tertiary',
    color?.tertiary || '#155eef'
  )
  document.documentElement.style.setProperty(
    '--error',
    color?.error || '#d92d20'
  )
  document.documentElement.style.setProperty(
    '--surface',
    color?.surface || '#F9FAFB'
  )
  document.documentElement.style.setProperty(
    '--fontPrimary',
    color?.fontPrimary || '#1d2939'
  )
  document.documentElement.style.setProperty(
    '--fontSecondary',
    color?.fontSecondary || '#909090'
  )
  document.documentElement.style.setProperty('--border', color?.border)
  document.documentElement.style.setProperty(
    '--background',
    color?.background || '#ffffff'
  )
  document.documentElement.style.setProperty('--fill', color?.fill || '#e3ecfc')
  const fillLight =
    mode === 'dark'
      ? chroma(getCSSVariable('--fill')).brighten(0.1).hex()
      : chroma(getCSSVariable('--fill')).darken(0.1).hex()

  document.documentElement.style.setProperty('--fillLight', fillLight)

  generateSupportingSidebarColors(
    color.primary,
    color.sidebarBackground,
    mode as string
  )

  const primary = getCSSVariable('--primary')

  const { primaryLight, primaryDark } = generateSupportingPrimaryColors(primary)
  document.documentElement.style.setProperty('--primaryLight', primaryLight)
  document.documentElement.style.setProperty('--primaryDark', primaryDark)

  Theme.state.isSideBarBackgroundDark = checkIsSideBackgroundDark()
  Theme.updateIsBackgroundSideBarSame()
}

export function getLighterDarkerColorThemeSelector(
  color: string,
  amount: number
): string {
  try {
    let lighterDarkerColor
    if (amount < 0) {
      // Generate darker color
      lighterDarkerColor = chroma(color).darken(Math.abs(amount)).hex()
    } else {
      // Generate lighter color
      lighterDarkerColor = chroma(color).brighten(amount).hex()
    }

    // Check if the generated color is close to white
    const isCloseToWhite = chroma.contrast(lighterDarkerColor, '#ffffff') < 0.5
    if (isCloseToWhite) {
      return '#f5f5f5' // Return off-white color
    }

    return lighterDarkerColor
  } catch (error) {
    console.error(color, error)
    return ''
  }
}

export function generateSupportingSidebarColors(
  sidebarSelectedItem: string,
  sidebarBackground: string,
  mode: string
): void {
  const saturation = chroma(sidebarBackground).get('hsl.s')

  // check if the sidebar and background colors light or dark
  const isColorLight = chroma(sidebarBackground).luminance() > 0.5
  const isSelectedItemLight = chroma(sidebarSelectedItem).luminance() > 0.5

  const sidebarFontPrimary = isColorLight
    ? chroma.mix(sidebarBackground, '#000000', 0.9, 'rgb').hex()
    : chroma.mix(sidebarBackground, '#ffffff', 0.9, 'rgb').hex()

  const sidebarFontSecondary = isColorLight
    ? chroma.mix(sidebarBackground, '#000000', 0.6, 'rgb').hex()
    : chroma.mix(sidebarBackground, '#ffffff', 0.6, 'rgb').hex()

  const sidebarFontOnSelectItem = isSelectedItemLight
    ? chroma.mix(sidebarSelectedItem, '#000000', 0.9, 'rgb').hex()
    : chroma.mix(sidebarSelectedItem, '#ffffff', 0.9, 'rgb').hex()

  const sidebarFill = isColorLight
    ? chroma(sidebarBackground).darken(0.5).hex()
    : chroma(sidebarBackground).brighten(0.5).hex()

  const sidebarBackgroundGradient = isColorLight
    ? saturation < 0.2
      ? chroma(sidebarBackground).darker(0.5).hex()
      : chroma(sidebarBackground).darker(0.5).saturate(0.5).hex()
    : chroma(sidebarBackground).brighten(0.3).saturate(0.3).hex()

  const sidebarBorderColor =
    mode === 'dark'
      ? chroma(sidebarBackground).brighten(0.6).hex()
      : chroma(sidebarBackground).darken(0.6).hex()

  document.documentElement.style.setProperty(
    '--sidebar-selected-item-font',
    sidebarFontOnSelectItem
  )

  document.documentElement.style.setProperty(
    '--sidebar-font-primary',
    sidebarFontPrimary
  )

  document.documentElement.style.setProperty(
    '--sidebar-font-secondary',
    sidebarFontSecondary
  )

  document.documentElement.style.setProperty('--sidebar-fill', sidebarFill)

  document.documentElement.style.setProperty(
    '--sidebar-background-gradient',
    sidebarBackgroundGradient
  )

  document.documentElement.style.setProperty(
    '--sidebar-border',
    sidebarBorderColor
  )
}

export function generateSupportingPrimaryColors(primary: string): {
  primaryLight: string
  primaryDark: string
} {
  const luminance = chroma(primary).luminance()
  const primaryLight = chroma(primary).brighten(1).hex()
  const primaryDark =
    luminance < 0.2
      ? chroma(primary).darken(0.3).hex() //If already a dark color
      : chroma(primary).get('hsl.s') < 0.2 //If color is not dark
      ? chroma(primary).darken(0.5).hex() //If color is not saturated
      : chroma(primary).darken(0.5).saturate(0.5).hex() //If color is saturated

  return {
    primaryLight,
    primaryDark,
  }
}

export const defaultLightColor = {
  background: '#ffffff',
  tertiary: '#155eef',
  surface: '#F9FAFB',
  fontPrimary: '#1d2939',
  fontSecondary: '#909090',
  border: '#e4e4e4',
  error: '#d92d20',
  fill: '#f2f2f2',
  primary: '#1d4981',
  sidebarBackground: '#ffffff',
}

export const defaultDarkColor = {
  background: '#242526',
  tertiary: '#93B3F3',
  surface: '#18191a',
  fontPrimary: '#e3e6ea',
  fontSecondary: '#b0b3b8',
  border: '#393a3b',
  error: '#d92d20',
  fill: '#3a3b3c',
  primary: '#1d4981',
  sidebarBackground: '#242526',
}
export function getDefaultColors(mode: string): { [key: string]: string } {
  if (app.state.isMobile) {
    defaultLightColor.primary = '#101828'
    defaultLightColor.fontSecondary = '#65676B'
    return defaultLightColor
  }
  if (mode === 'light') {
    return defaultLightColor
  } else {
    return defaultDarkColor
  }
}

export function handleColorsInitialization(): void {
  updateColorTokens()
  Theme.state.windowGradient =
    GroupState.state?.groupData?.branding?.color?.windowGradient
}

function toggleShimmerColorScheme(isDarkMode: boolean): void {
  const root = document.documentElement

  if (isDarkMode) {
    // Switch to light mode
    root.style.setProperty('--bg-color', '#f5f5f5')
    root.style.setProperty('--sidebar-bg', '#e0e0e0')
    root.style.setProperty('--main-bg', '#ffffff')
    root.style.setProperty('--shimmer-color', '#f0f0f0')
    root.style.setProperty('--shimmer-highlight', '#e0e0e0')
    root.style.setProperty('--text-color', '#333333')
    root.style.setProperty('--border-color', '#d0d0d0')
  } else {
    // Switch to dark mode
    root.style.setProperty('--bg-color', '#1a1a1a')
    root.style.setProperty('--sidebar-bg', '#2a2a2a')
    root.style.setProperty('--main-bg', '#333333')
    root.style.setProperty('--shimmer-color', '#444444')
    root.style.setProperty('--shimmer-highlight', '#555555')
    root.style.setProperty('--text-color', '#e0e0e0')
    root.style.setProperty('--border-color', '#444444')
  }
}
function getLightColor(hexColor) {
  // Convert HEX to RGB
  const hexToRgb = hex => {
    hex = hex.replace('#', '')
    if (hex.length === 8) {
      hex = hex.slice(0, 6) // Remove the alpha channel
    }
    if (hex.length === 3) {
      hex = hex
        .split('')
        .map(x => x + x)
        .join('') // Handle shorthand HEX (#abc)
    }
    const bigint = parseInt(hex, 16)
    return {
      r: (bigint >> 16) & 255,
      g: (bigint >> 8) & 255,
      b: bigint & 255,
    }
  }

  // Convert RGB to HSL
  const rgbToHsl = (r, g, b) => {
    r /= 255
    g /= 255
    b /= 255
    const max = Math.max(r, g, b)
    const min = Math.min(r, g, b)
    let h,
      s,
      l = (max + min) / 2

    if (max === min) {
      h = s = 0 // Achromatic
    } else {
      const d = max - min
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
      switch (max) {
        case r:
          h = (g - b) / d + (g < b ? 6 : 0)
          break
        case g:
          h = (b - r) / d + 2
          break
        case b:
          h = (r - g) / d + 4
          break
      }
      h *= 60
    }
    return { h, s, l }
  }

  // Convert HSL to RGB
  const hslToRgb = (h, s, l) => {
    const hueToRgb = (p, q, t) => {
      if (t < 0) t += 1
      if (t > 1) t -= 1
      if (t < 1 / 6) return p + (q - p) * 6 * t
      if (t < 1 / 2) return q
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6
      return p
    }

    let r, g, b
    if (s === 0) {
      r = g = b = l // Achromatic
    } else {
      const q = l < 0.5 ? l * (1 + s) : l + s - l * s
      const p = 2 * l - q
      r = hueToRgb(p, q, h / 360 + 1 / 3)
      g = hueToRgb(p, q, h / 360)
      b = hueToRgb(p, q, h / 360 - 1 / 3)
    }
    return {
      r: Math.round(r * 255),
      g: Math.round(g * 255),
      b: Math.round(b * 255),
    }
  }

  // Convert RGB to HEX
  const rgbToHex = ({ r, g, b }) => {
    return `#${((1 << 24) + (r << 16) + (g << 8) + b)
      .toString(16)
      .slice(1)
      .toUpperCase()}`
  }

  // Process the HEX color
  const { r, g, b } = hexToRgb(hexColor)
  const { h, s, l } = rgbToHsl(r, g, b)

  // Make the search bar lighter
  const newLightness = l > 0.5 ? Math.min(l - 0.1, 1) : Math.min(l + 0.15, 1) // Increase lightness more

  // Convert back to RGB, then HEX
  const adjustedRgb = hslToRgb(h, s, newLightness)
  return rgbToHex(adjustedRgb)
}

// Example Usage
